import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Crown, MapPin, Shield, Gem, Plus, RefreshCw, ExternalLink } from "lucide-react";

export default function FlexerBoard() {
  const { user, refresh } = useAuth();
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinAmount, setJoinAmount] = useState(6);
  const [incrementAmount, setIncrementAmount] = useState(1);
  const [joining, setJoining] = useState(false);
  const [incrementing, setIncrementing] = useState(false);
  const [myRank, setMyRank] = useState(null);         // 1‑based rank if on board
  const [myCard, setMyCard] = useState(null);        // own card object from board

  useEffect(() => {
    loadBoard();
  }, []);

  const loadBoard = async () => {
    try {
      const res = await api.get("/flexer/board");
      const boardData = res.data || [];
      setBoard(boardData);
      // Find own card
      const own = boardData.find(entry => entry.user_id === user?.user_id);
      if (own) {
        setMyCard(own);
        const rank = boardData.indexOf(own) + 1;
        setMyRank(rank);
      } else {
        setMyCard(null);
        setMyRank(null);
      }
    } catch (err) {
      toast.error("Failed to load Flexer Board");
    }
    setLoading(false);
  };

  const handleJoin = async () => {
    setJoining(true);
    try {
      await api.post(`/flexer/join?amount=${joinAmount}`);
      await refresh();
      toast.success("You're on the Flexer Board! 🎉");
      loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join");
    }
    setJoining(false);
  };

  const handleIncrement = async () => {
    setIncrementing(true);
    try {
      await api.post(`/flexer/increment?amount=${incrementAmount}`);
      await refresh();
      toast.success("Diamonds added to your Flexer card!");
      loadBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add diamonds");
    }
    setIncrementing(false);
  };

  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birth = new Date(dateOfBirth);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-2">
          <Crown className="w-8 h-8 text-yellow-500" />
          <h1 className="font-display text-3xl font-semibold">Flexer Board</h1>
        </div>
        <p className="text-sm text-[#6B6B70] mb-6">Top profiles on Haven. Join with diamonds to be seen first!</p>

        {/* User's own section – always visible */}
        <div className="bg-white border border-[#E7E5E0] rounded-2xl p-5 mb-6">
          <h3 className="font-semibold mb-3">Your Flexer Status</h3>
          <p className="text-sm text-gray-500 mb-3">You have 💎 {user?.diamonds || 0} diamonds</p>

          {myCard ? (
            <div className="flex items-center gap-4 mb-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center flex-shrink-0">
                #{myRank}
              </div>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0 border-2 border-yellow-400">
                  <img src={myCard.profile_image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{myCard.display_name}</div>
                  <div className="text-xs text-gray-500">
                    {myCard.country}{myCard.city ? `, ${myCard.city}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 text-yellow-600 font-bold text-lg flex-shrink-0">
                <Gem className="w-5 h-5" />
                {myCard.diamonds_committed}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 mb-3">You're not on the Flexer Board yet. Join to get seen first!</p>
          )}

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="6"
                value={joinAmount}
                onChange={(e) => setJoinAmount(parseInt(e.target.value) || 6)}
                className="w-16 px-2 py-1.5 border rounded-lg text-sm text-center"
              />
              <button
                onClick={handleJoin}
                disabled={joining}
                className="neo-btn bg-yellow-500 border-yellow-500 text-sm !py-2"
              >
                {joining ? "Joining..." : myCard ? "Add / Renew" : "Join"}
              </button>
            </div>
            {myCard && (
              <>
                <div className="w-px h-8 bg-gray-200 hidden sm:block" />
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    value={incrementAmount}
                    onChange={(e) => setIncrementAmount(parseInt(e.target.value) || 1)}
                    className="w-16 px-2 py-1.5 border rounded-lg text-sm text-center"
                  />
                  <button
                    onClick={handleIncrement}
                    disabled={incrementing}
                    className="neo-btn bg-purple-600 border-purple-600 text-sm !py-2"
                  >
                    {incrementing ? "Adding..." : "Add Diamonds"}
                  </button>
                </div>
              </>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-2">Minimum 6 diamonds to join. Renews for 30 days. Card expires after 12 months.</p>
        </div>

        {/* Leaderboard */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : board.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No one on the Flexer Board yet. Be the first!</div>
        ) : (
          <div className="space-y-4">
            {board.map((entry, index) => (
              <div key={entry.card_id} className={`bg-white border rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-all ${entry.user_id === user?.user_id ? 'border-yellow-400 ring-2 ring-yellow-200' : 'border-[#E7E5E0]'}`}>
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full font-bold text-sm flex items-center justify-center flex-shrink-0 ${entry.user_id === user?.user_id ? 'bg-yellow-100 text-yellow-700' : 'bg-purple-100 text-purple-700'}`}>
                  {index + 1}
                </div>

                {/* Avatar */}
                <Link to={`/find?profile=${entry.user_id}`} className="w-14 h-14 rounded-full overflow-hidden bg-[#F5F3EE] flex-shrink-0 border-2 border-yellow-400">
                  <img src={entry.profile_image} alt={entry.display_name} className="w-full h-full object-cover" />
                </Link>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">
                      {entry.display_name}
                      {entry.user_id === user?.user_id && (
                        <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-normal">You</span>
                      )}
                    </h3>
                    {entry.age && <span className="text-sm text-gray-500">{entry.age}</span>}
                    {entry.health_status && entry.health_status !== "none" && (
                      <span className="px-1.5 py-0.5 bg-red-50 text-red-600 rounded-full text-xs flex items-center gap-1">
                        <Shield className="w-3 h-3" />{entry.health_status}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                    <MapPin className="w-3 h-3" />{entry.country}{entry.city ? `, ${entry.city}` : ""}
                  </div>
                </div>

                {/* Diamonds count */}
                <div className="flex items-center gap-1 text-yellow-600 font-bold text-lg flex-shrink-0">
                  <Gem className="w-5 h-5" />
                  {entry.diamonds_committed}
                </div>

                {/* Profile link */}
                <Link
                  to={`/find?profile=${entry.user_id}`}
                  className="p-2 rounded-full hover:bg-purple-50 text-purple-600 flex-shrink-0"
                  title="View profile"
                >
                  <ExternalLink className="w-5 h-5" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}